import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import SubHeader from "components/common/SubHeader";
import CollapsibleSection from "./components/CollapsibleSection";
import ProductDetailSection from "./components/ProductDetailSection";
import DeliveryInfo from "./components/DeliveryInfo";
import PaymentInfo from "./components/PaymentInfo";
import LoadingSpinner from "components/common/LoadingSpiner";
import { Button } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTruck, faCheck, faStar } from "@fortawesome/free-solid-svg-icons";
import { getOrderDetail, confirmPurchase } from "api/orderHistory";
import { getCustomerInputPairs } from "utils/customerInput";
import { useSnackbar } from "contexts/SnackbarContext";
import "./orderDetail.scss";
import "dayjs/locale/ko";

dayjs.locale("ko");

const OrderDetail = () => {
    const { orderId } = useParams();
    const navigate = useNavigate();
    const { showSuccess, showError } = useSnackbar();
    const [orderData, setOrderData] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [orderProductOpen, setOrderProductOpen] = useState(true);
    const [paymentInfoOpen, setPaymentInfoOpen] = useState(true);

    const fetchOrderDetail = useCallback(async () => {
        setIsLoading(true);
        try {
            const res = await getOrderDetail(orderId);
            if (res) {
                setOrderData(res);
            }
        } catch (error) {
            console.error("주문 상세 조회 오류:", error);
        } finally {
            setIsLoading(false);
        }
    }, [orderId]);

    useEffect(() => {
        fetchOrderDetail();
    }, [fetchOrderDetail]);

    const formatDateTime = (dateString) => {
        if (!dateString) return "";
        return dayjs(dateString).format("YYYY.MM.DD (ddd) HH:mm");
    };

    const formatDate = (dateString) => {
        if (!dateString) return "";
        return dayjs(dateString).format("YYYY.MM.DD HH:mm:ss");
    };


    const handleConfirmPurchase = async () => {
        if (!window.confirm("구매를 확정하시겠습니까?\n확정 후에는 취소할 수 없습니다.")) {
            return;
        }

        try {
            const res = await confirmPurchase(orderId);
            showSuccess(res.message || "구매가 확정되었습니다.");
            fetchOrderDetail();
        } catch (error) {
            const errorMessage =
                error.response?.data?.message || "구매 확정에 실패했습니다.";
            showError(errorMessage);
        }
    };

    const canConfirmPurchase =
        orderData?.status === "CONFIRMED" &&
        orderData?.fulfillment_status === "DELIVERED";

    // 리뷰 작성 가능 여부: 배송완료 / 구매확정 상태에서만 (취소 등 다른 상태에서는 미노출)
    const canWriteReview = ["DELIVERED", "PURCHASE_CONFIRMED"].includes(
        orderData?.fulfillment_status
    );

    if (isLoading) {
        return (
            <div className="order-detail">
                <SubHeader title="주문 상세" />
                <LoadingSpinner />
            </div>
        );
    }

    if (!orderData) {
        return (
            <div className="order-detail">
                <SubHeader title="주문 상세" />
                <div className="empty-state">
                    <p>주문 정보를 불러올 수 없습니다.</p>
                </div>
            </div>
        );
    }

    // 새로운 데이터 구조에서 필요한 정보 추출
    const shippingInfo = orderData.order_details?.shipping_info || orderData.items?.[0]?.item_details?.shipping_info || {};

    // 배송비 계산 (모든 아이템의 배송비 합산)
    const totalShippingFee = orderData.items?.reduce((sum, item) => {
        return sum + parseInt(item.shipping_fee || 0);
    }, 0) || 0;

    // 상품 금액 (API의 total_amount)
    const productPrice = parseInt(orderData.total_amount || 0);

    // 배송지 정보
    const deliveryData = {
        name: shippingInfo.recipient_name || "",
        phone: shippingInfo.recipient_phone || "",
        address: shippingInfo.address || "",
        postal_code: shippingInfo.postal_code || "",
        memo: shippingInfo.shipping_memo || "",
        // 배송 정보
        courier: orderData.courier || "",
        tracking_number: orderData.tracking_number || "",
        tracking_url: orderData.tracking_url || ""
    };

    // 결제 정보
    const paymentData = {
        order_number: orderData.order_number || "",
        product_price: productPrice,
        shipping: totalShippingFee,
        total_amount: productPrice + totalShippingFee
    };

    console.log(orderData,'===')
    return (
        <div className="order-detail">
            <SubHeader title="주문 상세" />
            
            <div className="order-detail-content">
                {/* Order Header */}
                <div className="order-header">
                    <div className="order-date">{formatDate(orderData.created_at)}</div>
                    <div className="order-number">
                        주문번호 {orderData.order_number}
                    </div>
                </div>

                {/* Order Products Section */}
                <CollapsibleSection
                    title="주문 정보"
                    isOpen={orderProductOpen}
                    onToggle={() => setOrderProductOpen(!orderProductOpen)}
                >
                    <DeliveryInfo 
                        delivery={deliveryData}
                        confirmDeadline={orderData.purchase_confirmed_at ? formatDateTime(orderData.purchase_confirmed_at) : null}
                    />
                    
                    {/* 여러 상품 표시 */}
                    {orderData.items && orderData.items.length > 0 ? (
                        <div className="product-list">
                            {orderData.items.map((item, idx) => {
                                // 장바구니 주문은 item_details, 단건 주문은 order_details 에 스냅샷된다.
                                const itemPairs = getCustomerInputPairs(item.item_details);
                                const customerInputPairs =
                                    itemPairs.length > 0
                                        ? itemPairs
                                        : getCustomerInputPairs(orderData.order_details);
                                const productData = {
                                    id: item.item_id,
                                    name: item.product_name,
                                    image: item.thumbnail || orderData.product_info?.thumbnail || null,
                                    option: item.product_type_display || "",
                                    quantity: item.quantity,
                                    seller: orderData.product_info?.brand || "",
                                    unit_price: parseInt(item.unit_price || 0),
                                    price: parseInt(item.subtotal || 0),
                                    shipping_fee: parseInt(item.shipping_fee || 0),
                                    customerInputPairs,
                                };

                                return (
                                    <ProductDetailSection
                                        key={item.item_id || idx}
                                        product={productData}
                                    />
                                );
                            })}
                        </div>
                    ) : (
                        <ProductDetailSection
                            product={{
                                id: orderData.id,
                                name: orderData.product_info?.name || "",
                                image: orderData.product_info?.thumbnail || null,
                                option: "",
                                quantity: orderData.quantity || 0,
                                seller: orderData.product_info?.brand || "",
                                price: parseInt(orderData.total_amount || 0),
                                customerInputPairs: getCustomerInputPairs(orderData.order_details),
                            }}
                        />
                    )}
                </CollapsibleSection>

                {/* Action Buttons */}
                <div className="action-buttons">
                    {canConfirmPurchase && (
                        <Button
                            variant="contained"
                            className="confirm-purchase-btn"
                            onClick={handleConfirmPurchase}
                        >
                            <FontAwesomeIcon icon={faCheck} />
                            구매확정
                        </Button>
                    )}
                    {canWriteReview && orderData.items?.map((item) => (
                        !item.has_review && (
                            <Button
                                key={`review-${item.item_id}`}
                                variant="contained"
                                className="review-button"
                                onClick={() => navigate(`/review/write/${orderId}/${item.item_id}`, {
                                    state: {
                                        productName: item.product_name,
                                        productImage: orderData.product_info?.thumbnail,
                                        productId: item.product_id,
                                        productType: item.product_type || "products",
                                    }
                                })}
                            >
                                <div className="review-button-content">
                                    <FontAwesomeIcon icon={faStar} />
                                    <span className="review-text">리뷰 작성</span>
                                </div>
                            </Button>
                        )
                    ))}
                    <div className="secondary-buttons">
                        {orderData.tracking_url && (
                            <Button
                                variant="outlined"
                                className="secondary-btn"
                                onClick={() => window.open(orderData.tracking_url, '_blank')}
                            >
                                <FontAwesomeIcon icon={faTruck} />
                                배송조회
                            </Button>
                        )}
                        {/* <Button
                            variant="outlined"
                            className="secondary-btn"
                            onClick={handleAddToCart}
                        >
                            <FontAwesomeIcon icon={faShoppingCart} />
                            장바구니 담기
                        </Button> */}
                    </div>
                </div>

                {/* Payment Info Section */}
                <CollapsibleSection
                    title="결제정보"
                    isOpen={paymentInfoOpen}
                    onToggle={() => setPaymentInfoOpen(!paymentInfoOpen)}
                >
                    <PaymentInfo payment={paymentData} />
                </CollapsibleSection>

            </div>
        </div>
    );
};

export default OrderDetail;
